import {
  StateNode,
  TLPointerEventInfo,
  Vec,
  createShapeId,
  getIndexAbove,
  getIndexBetween,
  maybeSnapToGrid,
} from "tldraw"
import { LDPage } from "../../../shared/LDPage"
import { GeoShapeGeoStyle, LDGeoShape } from "../../../shared/LDGeoShape"
import { LDInstanceMeta } from "../../../shared/LDInstance"
import { Expr } from "../../../shared/Expr"
import { LDShape } from "../../../shared/LDShape"

export class Pointing extends StateNode {
  static override id = "pointing"

  fill = false

  override onPointerUp() {
    this.complete()
  }

  override onEnter(info: TLPointerEventInfo & { fill: boolean }) {
    this.fill = info.fill ?? false
  }

  override onPointerMove(info: TLPointerEventInfo) {
    if (this.editor.inputs.isDragging) {
      const { originPagePoint } = this.editor.inputs

      const id = createShapeId()

      const creatingMarkId = this.editor.markHistoryStoppingPoint(
        `creating_geo:${id}`,
      )
      const newPoint = maybeSnapToGrid(originPagePoint, this.editor)

      const instanceMeta = this.editor.getInstanceState().meta as LDInstanceMeta

      const pageMeta = (this.editor.getCurrentPage() as LDPage).meta
      const fill = this.fill
      const layersArray = Object.values(pageMeta.layers).sort()

      const currentLayer = pageMeta.currentLayer

      const currentLayerIndexInList = layersArray.findIndex(
        (l) => l.id === currentLayer,
      )
      //console.log("currentLayerIndex", layersArray[currentLayerIndexInList].index)
      const layerAbove = layersArray[currentLayerIndexInList + 1] ?? null
      //console.log("layerAbove", layerAbove)

      const highestShapeIndexOnCurrentLayer =
        this.editor
          .getCurrentPageShapes()
          .filter((s) => LDShape.getLayerId(s as LDShape) === currentLayer)
          .sort((a, b) => b.index.localeCompare(a.index))[0]?.index ??
        layersArray[currentLayerIndexInList].index

      const nextLayerIndex =
        layerAbove?.index ?? getIndexAbove(highestShapeIndexOnCurrentLayer)

      const index = getIndexBetween(
        highestShapeIndexOnCurrentLayer,
        nextLayerIndex,
      )
      this.editor
        .createShapes<LDGeoShape>([
          {
            id,
            type: "custom-geo",
            x: newPoint.x,
            y: newPoint.y,
            index,
            props: fill
              ? {
                  fill: true,
                  pageId: this.editor.getCurrentPageId(),
                  layer: pageMeta.currentLayer,
                  w: 1,
                  h: 1,
                  geo: this.editor.getStyleForNextShape(GeoShapeGeoStyle),
                  scale: this.editor.user.getIsDynamicResizeMode()
                    ? 1 / this.editor.getZoomLevel()
                    : 1,
                  parameterValues: {
                    roundness: Expr.lit(10),
                    fillColor: Expr.lit(instanceMeta.fillColor),
                    visible: Expr.lit(true),
                    opacity: Expr.lit(1),
                  },
                }
              : {
                  fill: false,
                  pageId: this.editor.getCurrentPageId(),
                  layer: pageMeta.currentLayer,
                  w: 1,
                  h: 1,
                  geo: this.editor.getStyleForNextShape(GeoShapeGeoStyle),
                  scale: this.editor.user.getIsDynamicResizeMode()
                    ? 1 / this.editor.getZoomLevel()
                    : 1,
                  parameterValues: {
                    roundness: Expr.lit(10),
                    strokeColor: Expr.lit(instanceMeta.strokeColor),
                    strokeWidth: Expr.lit(instanceMeta.strokeWidth),
                    visible: Expr.lit(true),
                    opacity: Expr.lit(1),
                  },
                },
          },
        ])
        .select(id)
        .setCurrentTool("select.resizing", {
          ...info,
          target: "selection",
          handle: "bottom_right",
          isCreating: true,
          creatingMarkId,
          creationCursorOffset: { x: 1, y: 1 },
          onInteractionEnd: this.fill ? "custom-geo.fill" : "custom-geo",
        })
    }
  }

  override onCancel() {
    this.cancel()
  }

  override onComplete() {
    this.complete()
  }

  override onInterrupt() {
    this.cancel()
  }

  private complete() {
    const { originPagePoint } = this.editor.inputs

    const id = createShapeId()

    this.editor.markHistoryStoppingPoint(`creating_geo:${id}`)

    const scale = this.editor.user.getIsDynamicResizeMode()
      ? 1 / this.editor.getZoomLevel()
      : 1

    const geo = this.editor.getStyleForNextShape(GeoShapeGeoStyle)

    const size =
      geo === "star"
        ? { w: 200, h: 190 }
        : geo === "cloud"
          ? { w: 300, h: 180 }
          : { w: 200, h: 200 }

    const pageMeta = (this.editor.getCurrentPage() as LDPage).meta
    this.editor.createShapes<LDGeoShape>([
      {
        id,
        type: "custom-geo",
        x: originPagePoint.x,
        y: originPagePoint.y,
        index: pageMeta.layers[pageMeta.currentLayer].index,
        props: {
          layer: pageMeta.currentLayer,
          geo: this.editor.getStyleForNextShape(GeoShapeGeoStyle),
          scale,
          ...size,
        },
      },
    ])

    const shape = this.editor.getShape<LDGeoShape>(id)!
    if (!shape) return

    const { w, h } = shape.props

    const delta = new Vec(w / 2, h / 2).mul(scale)
    const parentTransform = this.editor.getShapeParentTransform(shape)
    if (parentTransform) delta.rot(-parentTransform.rotation())
    const newPoint = maybeSnapToGrid(
      new Vec(shape.x - delta.x, shape.y - delta.y),
      this.editor,
    )
    this.editor.select(id)
    this.editor.updateShape<LDGeoShape>({
      id: shape.id,
      type: "custom-geo",
      x: newPoint.x,
      y: newPoint.y,
      props: {
        geo: this.editor.getStyleForNextShape(GeoShapeGeoStyle),
        w: w * scale,
        h: h * scale,
      },
    })

    if (this.editor.getInstanceState().isToolLocked) {
      this.parent.transition("idle", { fill: this.fill })
    } else {
      this.editor.setCurrentTool("select", {})
    }
  }

  private cancel() {
    // we should not have created any shapes yet, so no need to bail
    this.parent.transition("idle")
  }
}
